import { useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { KdsTicketItem } from "@/types/kds";

export function useKdsActions(restaurantId: string | null) {
  const logEvent = useCallback(async (
    orderId: string,
    orderItemId: string | null,
    productionLocationId: string,
    eventType: string,
    payload: Record<string, any> = {}
  ) => {
    if (!restaurantId) return;
    await supabase.from("kds_events").insert({
      restaurant_id: restaurantId,
      order_id: orderId,
      order_item_id: orderItemId,
      production_location_id: productionLocationId,
      event_type: eventType,
      payload,
      user_id: (await supabase.auth.getUser()).data.user?.id || null,
    } as any);
  }, [restaurantId]);

  const startItem = useCallback(async (item: KdsTicketItem) => {
    if (item.status !== 'new' && item.status !== 'recalled') return;
    const { error } = await supabase
      .from("kds_ticket_items")
      .update({ status: 'in_progress', started_at: new Date().toISOString(), updated_at: new Date().toISOString() } as any)
      .eq("id", item.id);
    if (error) { toast.error("Erro ao iniciar item"); return; }
    await logEvent(item.order_id, item.order_item_id, item.production_location_id, "item_started", { previous_status: item.status });
  }, [logEvent]);

  const doneItem = useCallback(async (item: KdsTicketItem) => {
    if (item.status === 'done') return;
    const { error } = await supabase
      .from("kds_ticket_items")
      .update({ status: 'done', done_at: new Date().toISOString(), updated_at: new Date().toISOString() } as any)
      .eq("id", item.id);
    if (error) { toast.error("Erro ao finalizar item"); return; }
    await logEvent(item.order_id, item.order_item_id, item.production_location_id, "item_done", { previous_status: item.status });
  }, [logEvent]);

  const recallItem = useCallback(async (item: KdsTicketItem) => {
    if (item.status !== 'done') return;
    const { error } = await supabase
      .from("kds_ticket_items")
      .update({ status: 'in_progress', done_at: null, updated_at: new Date().toISOString() } as any)
      .eq("id", item.id);
    if (error) { toast.error("Erro ao desfazer item"); return; }
    await logEvent(item.order_id, item.order_item_id, item.production_location_id, "item_recalled", { previous_status: 'done' });
  }, [logEvent]);

  const doneTicket = useCallback(async (items: KdsTicketItem[]) => {
    const pending = items.filter(i => i.status !== 'done');
    if (pending.length === 0) return;
    const now = new Date().toISOString();
    const { error } = await supabase
      .from("kds_ticket_items")
      .update({ status: 'done', done_at: now, updated_at: now } as any)
      .in("id", pending.map(i => i.id));
    if (error) { toast.error("Erro ao finalizar ticket"); return; }
    await logEvent(pending[0].order_id, null, pending[0].production_location_id, "ticket_done", { item_count: pending.length });
  }, [logEvent]);

  const recallTicket = useCallback(async (items: KdsTicketItem[]) => {
    const doneItems = items.filter(i => i.status === 'done');
    if (doneItems.length === 0) return;
    const now = new Date().toISOString();
    const { error } = await supabase
      .from("kds_ticket_items")
      .update({ status: 'in_progress', done_at: null, updated_at: now } as any)
      .in("id", doneItems.map(i => i.id));
    if (error) { toast.error("Erro ao desfazer ticket"); return; }
    await logEvent(doneItems[0].order_id, null, doneItems[0].production_location_id, "ticket_recalled", { item_count: doneItems.length });
  }, [logEvent]);

  return { startItem, doneItem, recallItem, doneTicket, recallTicket };
}
